import { getAccessToken, getUID } from '@/common/utils/accountUtil';
import { getCurrentTeamInfo, getTeamInfo } from '@/domains/team/teamService';
import type { TeamListItem } from '@/domains/team/types';
import { resetTeamSubscribe } from '$/common/utils/subscribeUtil';
import { useLanguageStore } from '$/store/languageStore';
import {
  setClientOGroupId,
  setClientOId,
  setClientOIsAdmin,
  setClientOTeamName,
  setRadishAmount,
} from '$/store/useOfficialStore';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TeamStore {
  teamInfo: TeamListItem | null;
  teamRoleText: () => string;
}

/**
 * 根据团队信息中的 role 字段派生角色展示文案。
 * @param teamInfo 当前团队信息，为 null 时返回空字符串
 */
export function getTeamRoleText(teamInfo: TeamListItem | null): string {
  if (!teamInfo) {
    return '';
  }

  const { smartcatTranslationText } = useLanguageStore.getState().languageStore;

  return Number(teamInfo.role) === 1
    ? smartcatTranslationText((v) => v.administrator)
    : smartcatTranslationText((v) => v.member);
}

/**
 * 根据当前团队身份生成埋点公共参数，空团队用空串覆盖旧团队值。
 */
export function getTeamTrackCommonParams(teamInfo: TeamListItem | null | undefined): { oid: string; is_admin: string; } {
  if (!teamInfo) {
    return {
      oid: '',
      is_admin: '',
    };
  }

  return {
    oid: String(teamInfo.oid),
    is_admin: Number(teamInfo.role) === 1 ? '1' : '0',
  };
}

/**
 * 将团队身份同步到 mtstat 公共参数，供团队版埋点统一上报。
 */
function syncTeamTrackCommonParams(teamInfo: TeamListItem | null | undefined): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.mtstat?.setCommonParams(getTeamTrackCommonParams(teamInfo));
}

/**
 * 将团队详情里的积分池剩余额同步到 SDK 顶部余额，避免会员个人余额覆盖团队余额。
 */
function syncTeamRadishAmount(teamInfo: TeamListItem | null | undefined): void {
  if (typeof teamInfo?.credit_remaining !== 'number' || !Number.isFinite(teamInfo.credit_remaining)) {
    return;
  }
  setRadishAmount(teamInfo.credit_remaining);
}

const useTeamStore = create<TeamStore>()(
  persist(
    (_, get): TeamStore => ({
      teamInfo: null,
      teamRoleText: () => getTeamRoleText(get().teamInfo),
    }),
    {
      name: 'useTeamStore',
      version: 1,
      partialize: (state) => ({ teamInfo: state.teamInfo }),
      onRehydrateStorage: () => (state) => {
        setClientOId(state?.teamInfo?.oid);
        setClientOGroupId(state?.teamInfo?.client_ogroupid);
        setClientOTeamName(state?.teamInfo?.name);
        setClientOIsAdmin(state?.teamInfo ? Number(state.teamInfo.role) === 1 : undefined);
        syncTeamTrackCommonParams(state?.teamInfo);
        syncTeamRadishAmount(state?.teamInfo);
      },
    },
  ),
);

export default useTeamStore;

/**
 * 监听其他标签页对 useTeamStore 的 localStorage 变更，并重新水合 store。
 * @returns 取消监听的清理函数
 */
export function subscribeTeamStoreFromStorage(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const storageKey = useTeamStore.persist.getOptions().name!;

  /**
   * 处理 storage 事件，在 key 匹配时从 localStorage 重新水合。
   */
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key !== storageKey) {
      return;
    }
    Promise.resolve(useTeamStore.persist.rehydrate()).catch((error: unknown) => {
      console.error('rehydrate useTeamStore error:', error);
    });
  };

  window.addEventListener('storage', handleStorageChange);
  return () => window.removeEventListener('storage', handleStorageChange);
}

/**
 * 更新当前选中的团队信息。
 */
export function setTeamInfo(teamInfo: TeamListItem) {
  const currentTeamInfo = useTeamStore.getState().teamInfo;
  if (
    currentTeamInfo?.oid !== teamInfo.oid
    || currentTeamInfo?.client_ogroupid !== teamInfo.client_ogroupid
  ) {
    resetTeamSubscribe();
  }
  useTeamStore.setState({ teamInfo });
  setClientOId(teamInfo.oid);
  setClientOGroupId(teamInfo.client_ogroupid);
  setClientOTeamName(teamInfo.name);
  setClientOIsAdmin(Number(teamInfo.role) === 1);
  syncTeamTrackCommonParams(teamInfo);
  syncTeamRadishAmount(teamInfo);
}

/**
 * 清空当前团队信息。
 */
export function clearTeamInfo() {
  if (useTeamStore.getState().teamInfo) {
    resetTeamSubscribe();
  }
  useTeamStore.setState({ teamInfo: null });
  setClientOId(undefined);
  setClientOGroupId(undefined);
  setClientOTeamName(undefined);
  setClientOIsAdmin(undefined);
  syncTeamTrackCommonParams(null);
}

/**
 * 根据登录态拉取当前团队身份和实时详情；未登录或个人账号时清空团队信息。
 */
export async function refreshTeamInfo(): Promise<void> {
  const token = getAccessToken();
  const uid = getUID();

  if (!token || !uid) {
    clearTeamInfo();
    return;
  }

  try {
    const res = await getCurrentTeamInfo();
    if (res.identity === 'team' && res.team) {
      // 先写入当前团队摘要以建立 Client-OID，再查询版本、积分和到期时间等实时详情。
      setTeamInfo(res.team);
      try {
        const detail = await getTeamInfo(res.team.oid);
        if (useTeamStore.getState().teamInfo?.oid === detail.oid) {
          setTeamInfo(detail);
        }
      } catch (error) {
        // 详情失败时保留 current 摘要，避免短暂网络异常清空已选择的团队上下文。
        console.error('refreshTeamInfo detail error:', error);
      }
      return;
    }
    clearTeamInfo();
  } catch (error) {
    console.error('refreshTeamInfo error:', error);
  }
}
